<!-- Floating Nova trigger (self-contained: start/stop recording, reopen analysis); draggable -->
<button
  *ngIf="showFab"
  type="button"
  class="nova-fab"
  [class.recording]="fabState === 'recording'"
  [class.processing]="fabState === 'processing'"
  [class.ready]="fabState === 'ready'"
  [style.top.px]="fabTop"
  [style.left.px]="fabLeft"
  [style.right]="'auto'"
  (pointerdown)="onFabPointerDown($event)"
  [attr.aria-label]="fabState === 'recording' ? ('NOVA_FAB_TRANSCRIBING' | translate) : fabState === 'processing' ? ('NOVA_FAB_ANALYZING' | translate) : fabState === 'ready' ? ('NOVA_FAB_SUMMARY_READY' | translate) : 'Ask-Nova'">
  <span class="nova-fab-ring"></span>
  <span class="nova-fab-core">
    <ion-icon *ngIf="fabState === 'idle' && !meetingAvailable" name="chatbubble-ellipses-outline"></ion-icon>
    <ion-icon *ngIf="fabState === 'idle' && meetingAvailable" name="sparkles-outline"></ion-icon>
    <ion-icon *ngIf="fabState === 'ready'" name="sparkles"></ion-icon>
    <ion-icon *ngIf="fabState === 'processing'" name="sync-outline" class="nova-fab-spin"></ion-icon>
    <span *ngIf="fabState === 'recording'" class="nova-fab-wave"><i></i><i></i><i></i><i></i></span>
  </span>
  <span class="nova-fab-timer" *ngIf="fabState === 'recording'">{{ recordingElapsed }}</span>
</button>

<div class="nova-voice-overlay" *ngIf="isOpen || isClosing" [class.desktop]="isDesktop" [class.nova-closing]="isClosing">
  <div class="nova-voice-backdrop" (click)="close()"></div>
  <div
    class="nova-voice-container"
    [class.desktop]="isDesktop"
    [class.nova-dragged]="panelLeft !== null && !isFullscreen"
    [style.left.px]="(panelLeft !== null && !isFullscreen) ? panelLeft : null"
    [style.top.px]="(panelTop !== null && !isFullscreen) ? panelTop : null">
  <!-- Branded gradient accent strip (Nova wordmark colors) -->
  <span class="nova-accent-strip" aria-hidden="true"></span>
  <!-- Header: beacon mode-switcher on the left, action cluster on the right.
       Doubles as the drag handle for repositioning the docked panel (desktop). -->
  <div class="nova-header" #header (pointerdown)="onHeaderPointerDown($event)">
    <!-- Header-left: a 2-tab segment when an artifact is in scope (replaces the
         wordmark), else the plain Ask-Nova wordmark. -->
    <div class="nova-header-left">
      <ng-container *ngIf="showSegment; else wordmark">
        <div *ngIf="headerLayout === 'tabs'" class="nova-seg" role="tablist" [attr.aria-label]="'NOVA_VIEWS' | translate">
          <button
            type="button"
            class="nova-seg__tab"
            role="tab"
            [class.nova-seg__tab--active]="mode === 'assistant'"
            [attr.aria-selected]="mode === 'assistant'"
            (click)="selectAssistantTab()">
            <span>Ask-Nova</span>
          </button>
          <button
            type="button"
            class="nova-seg__tab"
            role="tab"
            [class.nova-seg__tab--active]="mode !== 'assistant'"
            [attr.aria-selected]="mode !== 'assistant'"
            [attr.aria-haspopup]="hasMultipleContexts ? 'menu' : null"
            [attr.aria-expanded]="hasMultipleContexts ? isContextMenuOpen : null"
            (click)="onArtifactTabClick()">
            <span>{{ artifactTabLabel }}</span>
            <ion-icon *ngIf="hasMultipleContexts" class="nova-seg__chev" name="chevron-down" aria-hidden="true"></ion-icon>
          </button>
        </div>

        <!-- The two tabs as one dropdown, when the header is too narrow for both labels. -->
        <div *ngIf="headerLayout !== 'tabs'" class="nova-seg">
          <button
            type="button"
            class="nova-seg__tab nova-seg__tab--active"
            aria-haspopup="menu"
            [attr.aria-expanded]="isViewMenuOpen"
            [attr.aria-label]="'NOVA_VIEWS' | translate"
            (click)="isViewMenuOpen = !isViewMenuOpen">
            <span>{{ currentViewLabel }}</span>
            <ion-icon class="nova-seg__chev" name="chevron-down" aria-hidden="true"></ion-icon>
          </button>
        </div>

        <!-- Hidden copies at natural width, measured to choose the header layout. -->
        <div class="nova-seg nova-seg--sizer" #tabsSizer aria-hidden="true">
          <span class="nova-seg__tab"><span>Ask-Nova</span></span>
          <span class="nova-seg__tab">
            <span>{{ artifactTabLabel }}</span>
            <ion-icon *ngIf="hasMultipleContexts" class="nova-seg__chev" name="chevron-down"></ion-icon>
          </span>
        </div>
        <div class="nova-seg nova-seg--sizer nova-seg--sizer-stacked" #dropdownSizer aria-hidden="true">
          <span class="nova-seg__tab"><span>Ask-Nova</span><ion-icon class="nova-seg__chev" name="chevron-down"></ion-icon></span>
          <span class="nova-seg__tab"><span>{{ artifactTabLabel }}</span><ion-icon class="nova-seg__chev" name="chevron-down"></ion-icon></span>
        </div>
      </ng-container>

      <div class="nova-ctx-menu-backdrop" *ngIf="isViewMenuOpen && headerLayout !== 'tabs'" (click)="isViewMenuOpen = false"></div>
      <div class="nova-ctx-menu" *ngIf="isViewMenuOpen && headerLayout !== 'tabs'" role="menu" [attr.aria-label]="'NOVA_VIEWS' | translate">
        <button
          type="button"
          role="menuitemradio"
          [attr.aria-checked]="mode === 'assistant'"
          class="nova-ctx-menu__item"
          [class.nova-ctx-menu__item--current]="mode === 'assistant'"
          (click)="selectView(null)">
          <ion-icon name="sparkles-outline" class="nova-ctx-menu__icon" aria-hidden="true"></ion-icon>
          <span class="nova-ctx-menu__label">Ask-Nova</span>
          <span class="nova-ctx-menu__tag">{{ 'NOVA_CHAT' | translate }}</span>
          <ion-icon *ngIf="mode === 'assistant'" name="checkmark" class="nova-ctx-menu__check" aria-hidden="true"></ion-icon>
        </button>
        <button
          *ngFor="let c of contextChoices; trackBy: trackContextChoice"
          type="button"
          role="menuitemradio"
          [attr.aria-checked]="c.current"
          class="nova-ctx-menu__item"
          [class.nova-ctx-menu__item--current]="c.current"
          (click)="selectView(c)">
          <ion-icon [name]="c.icon" class="nova-ctx-menu__icon" aria-hidden="true"></ion-icon>
          <span class="nova-ctx-menu__label">{{ c.label }}</span>
          <span class="nova-ctx-menu__tag">{{ artifactTabLabel }}</span>
          <ion-icon *ngIf="c.current" name="checkmark" class="nova-ctx-menu__check" aria-hidden="true"></ion-icon>
        </button>
      </div>

      <!-- Insights ⌄ context switcher: pick which in-scope context's Insights to view. -->
      <div class="nova-ctx-menu-backdrop" *ngIf="isContextMenuOpen" (click)="isContextMenuOpen = false"></div>
      <div class="nova-ctx-menu" *ngIf="isContextMenuOpen" role="menu" [attr.aria-label]="'NOVA_SWITCH_CONTEXT' | translate">
        <button
          *ngFor="let c of contextChoices; trackBy: trackContextChoice"
          type="button"
          role="menuitemradio"
          [attr.aria-checked]="c.current"
          class="nova-ctx-menu__item"
          [class.nova-ctx-menu__item--current]="c.current"
          (click)="selectContextChoice(c)">
          <ion-icon [name]="c.icon" class="nova-ctx-menu__icon" aria-hidden="true"></ion-icon>
          <span class="nova-ctx-menu__label">{{ c.label }}</span>
          <span class="nova-ctx-menu__tag">{{ c.tag }}</span>
          <ion-icon *ngIf="c.current" name="checkmark" class="nova-ctx-menu__check" aria-hidden="true"></ion-icon>
        </button>
      </div>

      <ng-template #wordmark>
        <span class="nova-beacon nova-beacon--inert">
          <span class="nova-beacon__label nova-beacon__label--brand">Ask-Nova</span>
        </span>
      </ng-template>
    </div>

    <div class="nova-header-actions">
      <button
        *ngIf="isRecording && mode !== 'meeting'"
        #recordingChip
        type="button"
        class="nova-status-chip nova-status-chip--return"
        [attr.aria-label]="'NOVA_RETURN_TO_LIVE_MEETING' | translate"
        (click)="setMode('meeting'); meetingFlowState = 'live'">
        <span class="nova-status-chip__dot" aria-hidden="true"></span>
        {{ 'NOVA_TRANSCRIBING_ELAPSED' | translate: { time: recordingElapsed } }}
      </button>

      <button
        *ngIf="mode === 'assistant'"
        type="button"
        class="close-btn"
        [attr.aria-label]="'NOVA_NEW_CONVERSATION' | translate"
        [title]="'NOVA_NEW_CONVERSATION' | translate"
        (click)="newConversation()">
        <ion-icon name="create-outline"></ion-icon>
      </button>
      <ng-container *ngIf="headerLayout !== 'overflow'; else moreActions">
        <button
          *ngIf="mode === 'assistant' && hasConversationHistory"
          type="button"
          class="close-btn"
          [attr.aria-label]="'NOVA_CONVERSATION_HISTORY' | translate"
          [title]="'NOVA_CONVERSATION_HISTORY' | translate"
          (click)="openHistory()">
          <ion-icon name="time-outline"></ion-icon>
        </button>
        <button
          *ngIf="isDesktop"
          type="button"
          class="close-btn"
          [attr.aria-label]="(isFullscreen ? 'NOVA_EXIT_FULL_SCREEN' : 'NOVA_ENTER_FULL_SCREEN') | translate"
          [attr.aria-pressed]="isFullscreen"
          (click)="toggleFullscreen()">
          <ion-icon [name]="isFullscreen ? 'contract-outline' : 'expand-outline'"></ion-icon>
        </button>
        <button
          type="button"
          class="close-btn theme-btn"
          [attr.aria-label]="'NOVA_TOGGLE_THEME' | translate"
          [attr.aria-pressed]="!isDark"
          (click)="toggleTheme()">
          <ion-icon [name]="isDark ? 'moon-outline' : 'sunny-outline'"></ion-icon>
        </button>
      </ng-container>

      <!-- Secondary actions in one "more" menu, when the header is too narrow for their buttons. -->
      <ng-template #moreActions>
        <div class="nova-more">
          <button
            type="button"
            class="close-btn"
            aria-haspopup="menu"
            [attr.aria-expanded]="isMoreMenuOpen"
            [attr.aria-label]="'MORE' | translate"
            [title]="'MORE' | translate"
            (click)="isMoreMenuOpen = !isMoreMenuOpen">
            <ion-icon name="ellipsis-horizontal"></ion-icon>
          </button>
          <div class="nova-ctx-menu-backdrop" *ngIf="isMoreMenuOpen" (click)="isMoreMenuOpen = false"></div>
          <div class="nova-ctx-menu nova-ctx-menu--end" *ngIf="isMoreMenuOpen" role="menu" [attr.aria-label]="'MORE' | translate">
            <button
              *ngIf="mode === 'assistant' && hasConversationHistory"
              type="button"
              role="menuitem"
              class="nova-ctx-menu__item"
              (click)="isMoreMenuOpen = false; openHistory()">
              <ion-icon name="time-outline" class="nova-ctx-menu__icon" aria-hidden="true"></ion-icon>
              <span class="nova-ctx-menu__label">{{ 'NOVA_CONVERSATION_HISTORY' | translate }}</span>
            </button>
            <button
              *ngIf="isDesktop"
              type="button"
              role="menuitem"
              class="nova-ctx-menu__item"
              (click)="isMoreMenuOpen = false; toggleFullscreen()">
              <ion-icon [name]="isFullscreen ? 'contract-outline' : 'expand-outline'" class="nova-ctx-menu__icon" aria-hidden="true"></ion-icon>
              <span class="nova-ctx-menu__label">{{ (isFullscreen ? 'NOVA_EXIT_FULL_SCREEN' : 'NOVA_ENTER_FULL_SCREEN') | translate }}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              class="nova-ctx-menu__item"
              (click)="isMoreMenuOpen = false; toggleTheme()">
              <ion-icon [name]="isDark ? 'sunny-outline' : 'moon-outline'" class="nova-ctx-menu__icon" aria-hidden="true"></ion-icon>
              <span class="nova-ctx-menu__label">{{ (isDark ? 'NOVA_LIGHT_THEME' : 'NOVA_DARK_THEME') | translate }}</span>
            </button>
          </div>
        </div>
      </ng-template>
      <button type="button" class="close-btn" [attr.aria-label]="'CLOSE' | translate" (click)="close()">
        <ion-icon name="close"></ion-icon>
      </button>
    </div>
  </div>

  <!-- Meeting identity plate: below the header row.
       When the segment is shown the "Meeting" tab already communicates context,
       so we suppress the redundant eyebrow and keep only the HCP/subject title. -->
  <div class="nova-meeting-plate" *ngIf="mode === 'meeting'">
    <span class="nova-meeting-plate__eyebrow" *ngIf="!showSegment">{{ 'NOVA_INSIGHTS' | translate }}</span>
    <span class="nova-meeting-plate__title">{{ activity?.subject || hcpName || ('MEETING' | translate) }}</span>
  </div>

  <!-- Meeting mode: resolving / pre / live / processing / analysis -->
  <ng-container *ngIf="mode === 'meeting'">

  <!-- Resolving: checking for a previously-saved summary -->
  <div class="nova-meeting-resolving" *ngIf="meetingFlowState === 'resolving'" role="status" aria-live="polite">
    <ion-spinner name="dots" class="nova-meeting-resolving__spinner"></ion-spinner>
    <p class="nova-meeting-resolving__text">{{ 'NOVA_CHECKING_PREVIOUS_SUMMARY' | translate }}</p>
  </div>

  <!-- Pre: start panel (before transcription begins) -->
  <div class="nova-meeting-pre" *ngIf="meetingFlowState === 'pre'">
    <div class="nova-meeting-pre__body">
      <span class="nova-meeting-pre__hero" aria-hidden="true">
        <span class="nova-meeting-pre__halo"></span>
        <ion-icon name="mic-outline" class="nova-meeting-pre__icon"></ion-icon>
      </span>
      <h2 class="nova-meeting-pre__label">{{ hcpName || activity?.subject || ('MEETING' | translate) }}</h2>
      <p class="nova-meeting-pre__sub">{{ 'NOVA_MEETING_PRE_DESCRIPTION' | translate }}</p>

      <p class="nova-meeting-pre__error" *ngIf="meetingStartError" role="alert">
        <ion-icon name="alert-circle-outline" aria-hidden="true"></ion-icon>
        {{ meetingStartError }}
      </p>

      <button type="button" class="nova-primary-btn" (click)="startMeeting()">
        <ion-icon name="document-text-outline" aria-hidden="true"></ion-icon>
        {{ 'NOVA_TRANSCRIBE' | translate }}
      </button>

      <p class="nova-meeting-pre__secure">
        <ion-icon name="shield-checkmark-outline" aria-hidden="true"></ion-icon>
        {{ 'NOVA_PROCESSED_SECURELY' | translate }}
      </p>
    </div>
  </div>

  <!-- Live: privacy-positive capture indicator + Stop & analyze.
       The transcript text is intentionally hidden so the rep (and the HCP, if
       glanced over the shoulder) can see Ask-Nova is listening but nothing is
       displayed or persisted until analysis is requested. -->
  <div class="nova-meeting-live" *ngIf="meetingFlowState === 'live'">
    <div class="nova-capture" role="status" aria-live="polite"
         [attr.aria-label]="'NOVA_CAPTURING_ELAPSED' | translate: { time: recordingElapsed }">
      <div class="nova-capture__visual" aria-hidden="true">
        <span class="nova-capture__ripple"></span>
        <span class="nova-capture__ripple nova-capture__ripple--lag"></span>
        <span class="nova-capture__orb">
          <ion-icon name="mic"></ion-icon>
        </span>
        <span class="nova-capture__wave">
          <i></i><i></i><i></i><i></i><i></i><i></i><i></i>
        </span>
      </div>
      <p class="nova-capture__label">{{ 'NOVA_CAPTURING' | translate }}</p>
      <p class="nova-capture__timer">{{ recordingElapsed }}</p>
      <p class="nova-capture__privacy">
        <ion-icon name="lock-closed-outline" aria-hidden="true"></ion-icon>
        {{ 'NOVA_CAPTURE_PRIVACY' | translate }}
      </p>
    </div>

    <button type="button" class="nova-primary-btn nova-primary-btn--stop" (click)="stopMeeting()">
      <ion-icon name="checkmark-circle-outline" aria-hidden="true"></ion-icon>
      {{ 'NOVA_STOP_AND_ANALYZE' | translate }}
    </button>
  </div>

  <!-- Processing: analysis in flight -->
  <div class="nova-meeting-processing" *ngIf="meetingFlowState === 'processing'" role="status" aria-live="polite">
    <ion-spinner name="dots" class="nova-meeting-processing__spinner"></ion-spinner>
    <p class="nova-meeting-processing__text">{{ 'NOVA_ANALYZING' | translate }}</p>
  </div>

  <!-- Analysis: page tabs + analysis pages -->
  <ng-container *ngIf="meetingFlowState === 'analysis'">
  <!-- Page nav row: tabs/dots -->
  <div class="nova-analysis-bar">
    <div class="page-nav" role="tablist" [attr.aria-label]="'NOVA_ANALYSIS_SECTIONS' | translate">
      <button
        *ngFor="let labelKey of pageLabelKeys; let i = index"
        class="page-tab"
        role="tab"
        [attr.aria-selected]="currentPage === i"
        [attr.aria-label]="labelKey | translate"
        [class.active]="currentPage === i"
        [class.disabled]="i > 0 && !analysisData && !isProcessing"
        (click)="(analysisData || isProcessing || i === 0) ? goToPage(i) : null">
        <span class="tab-dot"></span>
        <span class="tab-label" *ngIf="isDesktop">{{ labelKey | translate }}</span>
      </button>
    </div>
  </div>

  <!-- Content Pages -->
  <div class="nova-content">
    <!-- Mobile: Swiper -->
    <swiper-container
      *ngIf="!isDesktop"
      #swiper
      [attr.slides-per-view]="1"
      [attr.allow-touch-move]="!!analysisData || isProcessing"
      (swiperslidechange)="onSlideChange()">

      <swiper-slide>
        <nova-conversational
          [hcpName]="hcpName"
          [capturedTranscript]="capturedTranscript"
          [analysisData]="analysisData"
          [isProcessing]="isProcessing"
          [responseId]="meetingSummaryResponseId"
          [usage]="meetingSummaryUsage"
          (feedback)="onCardFeedback($event)"
          (reAnalyze)="onReanalyze()"
          (reRecord)="startNewRecording()">
        </nova-conversational>
      </swiper-slide>

      <swiper-slide>
        <nova-call-analysis
          [data]="analysisData"
          [isProcessing]="isProcessing"
          (applySelections)="onApplySelections($event)">
        </nova-call-analysis>
      </swiper-slide>

      <swiper-slide>
        <nova-coaching
          [data]="analysisData"
          [isProcessing]="isProcessing">
        </nova-coaching>
      </swiper-slide>
    </swiper-container>

    <!-- Desktop: Conditional pages -->
    <div *ngIf="isDesktop" class="desktop-page">
      <nova-conversational
        *ngIf="currentPage === 0"
        [hcpName]="hcpName"
        [capturedTranscript]="capturedTranscript"
        [analysisData]="analysisData"
        [isProcessing]="isProcessing"
        [responseId]="meetingSummaryResponseId"
        [usage]="meetingSummaryUsage"
        (feedback)="onCardFeedback($event)"
        (reAnalyze)="onReanalyze()"
        (reRecord)="startNewRecording()">
      </nova-conversational>

      <ng-container *ngIf="currentPage === 1">
        <nova-call-analysis
          [data]="analysisData"
          [isProcessing]="isProcessing"
          (applySelections)="onApplySelections($event)">
        </nova-call-analysis>
      </ng-container>

      <nova-coaching
        *ngIf="currentPage === 2"
        [data]="analysisData"
        [isProcessing]="isProcessing">
      </nova-coaching>
    </div>
  </div>
  </ng-container>
  <!-- /analysis -->
  </ng-container>
  <!-- /meeting mode -->

  <!-- Record mode: per-record AI summary; Discuss routes to tool-aware assistant -->
  <div class="nova-record-view" *ngIf="mode === 'record' && record">
    <nova-record-summary
      [kind]="record.kind"
      [label]="record.label"
      [summary]="recordSummary"
      [responseId]="recordSummaryResponseId"
      [usage]="recordSummaryUsage"
      [isProcessing]="recordFlowState === 'processing'"
      [lastAnalyzedAt]="recordLastAnalyzedAt"
      (analyze)="onAnalyzeRecord()"
      (openChat)="discussRecord()"
      (openEntity)="onOpenEntity($event)"
      (action)="onCardAction($event)"
      (feedback)="onCardFeedback($event)">
    </nova-record-summary>
  </div>

  <!-- Assistant mode: general chat -->
  <div class="nova-assistant-view" *ngIf="mode === 'assistant'">
    <nova-assistant-chat
      [messages]="chatMessages"
      [isSending]="isSendingChat"
      [isHistoryLoading]="isLoadingChat"
      [isOffline]="isOffline"
      [attachments]="assistantAttachments"
      [welcome]="welcomeCard"
      [isWelcomeLoading]="isWelcomeLoading"
      [isToppingUpWelcome]="isToppingUpWelcome"
      (topUpWelcome)="onTopUpWelcome()"
      (sendMessage)="onChatSend($event)"
      (voiceError)="onVoiceError($event)"
      (attachClick)="openPicker()"
      (removeAttachment)="onRemoveAttachment($event)"
      (openEntity)="onOpenEntity($event)"
      (action)="onCardAction($event)"
      (feedback)="onCardFeedback($event)">
    </nova-assistant-chat>
  </div>

  <!-- Bottom sheets: float over the panel (don't push the chat thread). -->
  <div class="nova-sheet-overlay" *ngIf="isPickerOpen" (click)="isPickerOpen = false">
    <nova-attach-picker
      class="nova-sheet"
      (click)="$event.stopPropagation()"
      [contextAttachments]="contextAttachments"
      [attached]="assistantAttachments"
      (toggle)="onAttach($event)"
      (pick)="onAttachAndClose($event)"
      (dismiss)="isPickerOpen = false">
    </nova-attach-picker>
  </div>

  <div class="nova-sheet-overlay" *ngIf="isHistoryOpen" (click)="isHistoryOpen = false">
    <nova-conversation-list
      class="nova-sheet"
      (click)="$event.stopPropagation()"
      (resume)="onResumeConversation($event)"
      (newConversation)="newConversation()"
      (deleteConversation)="onDeleteConversation($event)"
      (dismiss)="isHistoryOpen = false">
    </nova-conversation-list>
  </div>
  </div>
</div>
